import React, { useState } from 'react'

import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material'
import { useCustomerId } from 'features/customers/hooks/useCustomerId'
import { Link } from 'react-router-dom'
import { UrlProvider } from 'urls'

type CustomerRevealApiExtraQuotaModalProps = {
  open: boolean
  onClose: () => void
  onConfirm: (value: number) => void
  currentCredits: number
  monthlyLimit: number
  disabled?: boolean
}

const CustomerRevealApiExtraQuotaModal = ({
  currentCredits,
  disabled,
  monthlyLimit,
  onClose,
  onConfirm,
  open,
}: CustomerRevealApiExtraQuotaModalProps) => {
  const customerId = useCustomerId()
  // Empty rather than a pre-filled 0: typing in front of that 0 multiplies the value by ten.
  const [value, setValue] = useState(currentCredits ? String(currentCredits) : '')

  // Digit-only: allows 0 (removes the credits) and positive integers; blocks blank/negatives/decimals/scientific notation.
  const isValid = /^\d+$/.test(value)
  const credits = isValid ? Number(value) : 0

  return (
    <Dialog onClose={onClose} open={open}>
      <DialogTitle>Set temporary credits</DialogTitle>
      <DialogContent sx={{ width: 450 }}>
        <DialogContentText sx={{ mb: 2 }}>
          Credits apply to this month only, on top of the monthly limit of {monthlyLimit.toLocaleString()}. To change
          the monthly limit itself, use the{' '}
          <Link onClick={onClose} to={UrlProvider.getUrl('customer', { id: customerId, tab: 'api' })}>
            API tab
          </Link>
          .
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          helperText={
            isValid &&
            `${credits.toLocaleString()} credits, ${(monthlyLimit + credits).toLocaleString()} in total this month`
          }
          label="Temporary credits"
          onChange={event => setValue(event.target.value)}
          placeholder="0"
          slotProps={{ htmlInput: { min: 0, step: 1 } }}
          type="number"
          value={value}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="primary" disabled={disabled || !isValid} onClick={() => onConfirm(credits)}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CustomerRevealApiExtraQuotaModal
